import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Loader2, X, MessageSquare, Zap, Shield } from 'lucide-react'
import { SUMIT_WHATSAPP_URL } from '@/lib/supabase'

interface Plan {
  id: string
  slug: string
  name: string
  name_he: string
  description_he: string
  amount_agorot: number
  features: string[]
  limits: Record<string, unknown>
  is_featured: boolean
  sort_order: number
}

const PLAN_ICONS: Record<string, React.ReactNode> = {
  basic: <MessageSquare size={28} />,
  pro: <Zap size={28} />,
  enterprise: <Shield size={28} />,
}

async function fetchPlans(): Promise<Plan[]> {
  const res = await fetch(SUMIT_WHATSAPP_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'get_plans' }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const json = await res.json()
  return (json.plans ?? []) as Plan[]
}

async function createPayment(planId: string, email: string, name: string, phone: string) {
  const origin = window.location.origin
  const res = await fetch(SUMIT_WHATSAPP_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'create_payment',
      planId,
      email,
      name,
      phone: phone || undefined,
      successUrl: `${origin}/payment/callback`,
      cancelUrl: `${origin}/payment/cancel`,
    }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `HTTP ${res.status}`)
  }
  return (await res.json()) as { paymentUrl: string; paymentId: string; orgId: string }
}

function PurchaseModal({
  plan,
  onClose,
}: {
  plan: Plan
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const result = await createPayment(plan.id, email, name, phone)
      window.location.href = result.paymentUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה לא צפויה')
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-2xl p-6"
        style={{ background: '#1a1d27', border: '1px solid #2d3148' }}
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <button
          onClick={onClose}
          className="absolute top-4 left-4 p-1.5 rounded-lg transition-colors hover:bg-white/10"
          style={{ color: '#9ca3af' }}
        >
          <X size={18} />
        </button>

        <h3 className="text-lg font-bold text-white mb-1">הזמנת מסלול {plan.name_he}</h3>
        <p className="text-sm mb-5" style={{ color: '#9ca3af' }}>
          ₪{(plan.amount_agorot / 100).toLocaleString('he-IL')}/חודש
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#d1d5db' }}>
              שם מלא <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="ישראל ישראלי"
              className="w-full px-4 py-2.5 rounded-lg text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-emerald-500 transition text-right"
              style={{ background: '#252836', border: '1px solid #3f4258' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#d1d5db' }}>
              אימייל <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="email@example.com"
              dir="ltr"
              className="w-full px-4 py-2.5 rounded-lg text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-emerald-500 transition text-left"
              style={{ background: '#252836', border: '1px solid #3f4258' }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#d1d5db' }}>
              טלפון <span className="text-xs" style={{ color: '#6b7280' }}>(אופציונלי)</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="050-1234567"
              dir="ltr"
              className="w-full px-4 py-2.5 rounded-lg text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-emerald-500 transition text-left"
              style={{ background: '#252836', border: '1px solid #3f4258' }}
            />
          </div>

          {error && (
            <div
              className="px-4 py-3 rounded-lg text-sm text-right"
              style={{ background: '#ef444420', color: '#f87171', border: '1px solid #ef444440' }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-bold text-white transition flex items-center justify-center gap-2"
            style={{ background: '#10b981', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                מעבד...
              </>
            ) : (
              'המשך לתשלום'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default function PricingPage() {
  const navigate = useNavigate()
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await fetchPlans()
      setPlans(data.sort((a, b) => a.sort_order - b.sort_order))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'שגיאה בטעינת מסלולים')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="min-h-screen" style={{ background: '#0f1117' }} dir="rtl" lang="he">
      {/* Nav bar */}
      <nav
        className="sticky top-0 z-30 flex items-center justify-between px-6 py-4"
        style={{ background: '#0f1117e6', backdropFilter: 'blur(12px)', borderBottom: '1px solid #2d3148' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
            style={{ background: '#10b981' }}
          >
            W
          </div>
          <span className="text-white font-semibold">WhatsApp Service</span>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="text-sm px-4 py-2 rounded-lg transition-colors"
          style={{ color: '#9ca3af', border: '1px solid #2d3148' }}
        >
          כניסת מנהלים
        </button>
      </nav>

      {/* Hero */}
      <header className="text-center px-6 pt-16 pb-12 max-w-3xl mx-auto">
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6"
          style={{ background: '#10b98120', color: '#4ade80', border: '1px solid #10b98140' }}
        >
          <MessageSquare size={16} />
          שירות WhatsApp של Otomator
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4">
          חבר את העסק שלך ל-WhatsApp
          <br />
          <span style={{ color: '#4ade80' }}>בכמה דקות</span>
        </h1>
        <p className="text-base sm:text-lg max-w-xl mx-auto" style={{ color: '#9ca3af' }}>
          שלח ותקבל הודעות WhatsApp אוטומטיות, קבל webhook בזמן אמת, נהל מספר קווים — הכל דרך API אחד פשוט.
        </p>
      </header>

      {/* Plans */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-emerald-400" size={40} />
          </div>
        )}

        {error && (
          <div
            className="max-w-md mx-auto text-center px-6 py-4 rounded-xl text-sm"
            style={{ background: '#ef444420', color: '#f87171', border: '1px solid #ef444440' }}
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {plans.map((plan) => {
              const featured = plan.is_featured
              const price = plan.amount_agorot / 100
              const features: string[] = Array.isArray(plan.features) ? plan.features : []

              return (
                <div
                  key={plan.id}
                  className="relative flex flex-col rounded-2xl p-6 transition-transform hover:-translate-y-1"
                  style={{
                    background: featured ? '#1a2e25' : '#1a1d27',
                    border: featured ? '2px solid #10b981' : '1px solid #2d3148',
                    boxShadow: featured ? '0 0 40px rgba(16,185,129,0.12)' : 'none',
                  }}
                >
                  {featured && (
                    <span
                      className="absolute -top-3 right-6 px-3 py-0.5 rounded-full text-xs font-bold"
                      style={{ background: '#10b981', color: '#052e16' }}
                    >
                      מומלץ
                    </span>
                  )}

                  <div className="flex items-center gap-3 mb-4 mt-1">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{
                        background: featured ? '#10b98125' : '#25283680',
                        color: featured ? '#4ade80' : '#9ca3af',
                        border: featured ? '1px solid #10b98150' : '1px solid #2d3148',
                      }}
                    >
                      {PLAN_ICONS[plan.slug] ?? <MessageSquare size={28} />}
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">{plan.name_he}</h3>
                      <p className="text-xs" style={{ color: '#6b7280' }}>
                        {plan.description_he}
                      </p>
                    </div>
                  </div>

                  <div className="mb-5">
                    <span className="text-3xl font-extrabold text-white">
                      ₪{price.toLocaleString('he-IL')}
                    </span>
                    <span className="text-sm mr-1" style={{ color: '#6b7280' }}>
                      /חודש
                    </span>
                  </div>

                  <ul className="flex-1 space-y-2.5 mb-6">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#d1d5db' }}>
                        <Check
                          size={16}
                          className="mt-0.5 flex-shrink-0"
                          style={{ color: featured ? '#4ade80' : '#6b7280' }}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => setSelectedPlan(plan)}
                    className="w-full py-3 rounded-xl font-bold text-sm transition-colors"
                    style={{
                      background: featured ? '#10b981' : '#252836',
                      color: featured ? '#fff' : '#d1d5db',
                      border: featured ? 'none' : '1px solid #3f4258',
                    }}
                  >
                    {featured ? 'התחל עכשיו' : 'בחר מסלול'}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer
        className="text-center text-xs py-6"
        style={{ color: '#4b5563', borderTop: '1px solid #1f2233' }}
      >
        © {new Date().getFullYear()} Otomator · כל הזכויות שמורות ·{' '}
        <a href="mailto:nizan@otomator.co.il" className="hover:text-white transition-colors">
          צור קשר
        </a>
      </footer>

      {selectedPlan && <PurchaseModal plan={selectedPlan} onClose={() => setSelectedPlan(null)} />}
    </div>
  )
}
